import type PricingArchitecture from '@app/model/pricing-automation/PricingArchitecture';
import type PricingArchitectureDerivativeRule from '@app/model/pricing-automation/PricingArchitectureDerivativeRule';
import type PricingStrategyRuleInput from '@app/model/pricing-automation/input/PricingStrategyRuleInput';
import PricingArchitectureDerivativeRuleType from '../../../../../../model/pricing-automation/PricingArchitectureDerivativeRuleType';
import PricingStrategyRuleType from '../../../../../../model/pricing-automation/PricingStrategyRuleType';

export type RoomLimitPreview = {
	roomId: number;
	roomName: string;
	rateType?: string | null;
	integrationId?: string | null;
	rateName?: string | null;
	board?: number | null;
	adjustment: string;
	minimum?: number;
	maximum?: number;
};

const ruleLimit = (rule: PricingStrategyRuleInput): number | undefined =>
	rule.type === PricingStrategyRuleType.DYNAMIC ? rule.threshold : rule.amount;

const adjustedLimit = (limit: number | undefined, rule?: PricingArchitectureDerivativeRule): number | undefined => {
	if (limit === undefined || !rule || rule.type === PricingArchitectureDerivativeRuleType.SAME) return limit;
	if (rule.value === undefined) return undefined;
	return rule.type === PricingArchitectureDerivativeRuleType.PERCENTAGE
		? limit + (limit * rule.value / 100)
		: limit + rule.value;
};

export const adjustmentLabel = (rule?: PricingArchitectureDerivativeRule): string => {
	if (!rule) return '—';
	if (rule.type === PricingArchitectureDerivativeRuleType.SAME) return 'Same Price';
	if (rule.value == null) return '—';
	const value = rule.value;
	const formattedValue = `${value > 0 ? '+' : ''}${value}`;
	return rule.type === PricingArchitectureDerivativeRuleType.PERCENTAGE ? `${formattedValue}%` : formattedValue;
};

export const createRoomLimitPreview = (
	architecture: PricingArchitecture,
	minimum: PricingStrategyRuleInput,
	maximum: PricingStrategyRuleInput,
): RoomLimitPreview[] => {
	const minimumLimit = ruleLimit(minimum);
	const maximumLimit = ruleLimit(maximum);

	return [
		{
			roomId: architecture.anchor.id,
			roomName: architecture.anchor.roomName,
			rateType: architecture.anchor.rateType,
			integrationId: architecture.anchor.integrationId,
			rateName: architecture.anchor.rateName,
			board: architecture.anchor.board,
			adjustment: adjustmentLabel(),
			minimum: minimumLimit,
			maximum: maximumLimit,
		},
		...architecture.derivatives.map(derivative => ({
			roomId: derivative.pricingRoom.id,
			roomName: derivative.pricingRoom.roomName,
			rateType: derivative.pricingRoom.rateType,
			integrationId: derivative.pricingRoom.integrationId,
			rateName: derivative.pricingRoom.rateName,
			board: derivative.pricingRoom.board,
			adjustment: adjustmentLabel(derivative.rule),
			minimum: minimum.followsRoomAdjustments ? adjustedLimit(minimumLimit, derivative.rule) : minimumLimit,
			maximum: maximum.followsRoomAdjustments ? adjustedLimit(maximumLimit, derivative.rule) : maximumLimit,
		})),
	];
};
